import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Upload, Loader2, Mail, MessageSquare, Image as ImageIcon, LogOut, Lock, Package, Tag, Instagram, Star, Trash2 } from "lucide-react";
import { useMediaContext } from "@/context/MediaContext";
import { PRODUCTS, COLLECTIONS, HERO_IMAGE, PACKAGING_IMAGE, BRAND_STORY_IMAGE } from "@/data/products";
import { AdminDiscounts } from "@/components/AdminDiscounts";
import AdminInstagram from "@/components/AdminInstagram";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const withAuth = { withCredentials: true };

const SLOT_GROUPS = [
  {
    group: "Hero Banners",
    slots: [{ key: "hero_banner", label: "Homepage Hero", fallback: HERO_IMAGE }],
  },
  {
    group: "Product Bottle Images",
    slots: PRODUCTS.map((p) => ({ key: `product_${p.slug.replace(/-/g, "_")}`, label: p.name, fallback: p.image })),
  },
  {
    group: "Collection Banners",
    slots: Object.entries(COLLECTIONS).map(([slug, c]) => ({ key: `collection_${slug.replace("-", "_")}`, label: c.title, fallback: c.image })),
  },
  {
    group: "Packaging Images",
    slots: [{ key: "packaging", label: "Packaging Showcase", fallback: PACKAGING_IMAGE }],
  },
  {
    group: "Brand Story Images",
    slots: [{ key: "brand_story", label: "About / Brand Story", fallback: BRAND_STORY_IMAGE }],
  },
  {
    group: "Instagram Feed (homepage strip)",
    slots: [1, 2, 3, 4, 5, 6].map((n) => ({ key: `instagram_${n}`, label: `Instagram Post ${n}`, fallback: null })),
  },
];

const MediaSlot = ({ slot, current, onUploaded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/media/upload/${slot.key}`, fd, withAuth);
      toast.success(`${slot.label} image updated`);
      onUploaded();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={`media-slot-${slot.key}`} className="border border-black/10 bg-white/40">
      <div className="aspect-[4/3] overflow-hidden bg-[#EFE9E1]">
        {current || slot.fallback ? (
          <img src={current || slot.fallback} alt={slot.label} loading="lazy" className="h-full w-full object-cover" />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-black/25">
            <Instagram size={28} strokeWidth={1} />
          </div>
        )}
      </div>
      <div className="p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-medium">{slot.label}</p>
          <p className="text-[0.62rem] uppercase tracking-[0.2em] text-black/40 mt-0.5">
            {current ? "Custom image" : "Placeholder"}
          </p>
        </div>
        <button
          data-testid={`media-upload-button-${slot.key}`}
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="btn-ink !px-4 !py-2.5 disabled:opacity-50"
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} strokeWidth={1.5} />}
          {busy ? "Uploading" : "Replace"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="hidden"
          data-testid={`media-file-input-${slot.key}`}
          onChange={(e) => upload(e.target.files?.[0])}
        />
      </div>
    </div>
  );
};

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

const AdminLogin = ({ onSuccess }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, withAuth);
      onSuccess(data);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="pt-24 md:pt-32 min-h-screen">
      <div className="max-w-md mx-auto px-5 pb-28">
        <div className="flex items-center gap-3">
          <Lock size={18} strokeWidth={1.5} className="text-[#BA8D53]" />
          <p className="overline-label">Restricted</p>
        </div>
        <h1 className="font-serif-brand text-5xl font-light mt-4">Admin Login</h1>
        <form onSubmit={submit} data-testid="admin-login-form" className="mt-12 space-y-8">
          <input required type="email" data-testid="admin-login-email-input" className="input-minimal" placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)} />
          <input required type="password" data-testid="admin-login-password-input" className="input-minimal" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          {error && <p data-testid="admin-login-error" className="text-sm text-red-700">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-submit-button" className="btn-ink disabled:opacity-50">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
      </div>
    </main>
  );
};

const STATUS_BADGE = {
  paid: "bg-[#BA8D53] text-white",
  failed: "bg-red-700 text-white",
};

const ShopifyBadge = ({ order }) => {
  if (order.shopify_order_id) {
    return <span className="px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.18em] bg-[#111] text-[#F7F3EE]">Shopify ✓</span>;
  }
  if (order.status === "paid") {
    return <span className="px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.18em] bg-black/5 text-black/40">Not in Shopify</span>;
  }
  return null;
};

export default function Admin() {
  const [tab, setTab] = useState("media");
  const [subs, setSubs] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [authed, setAuthed] = useState(null);
  const { media, refresh } = useMediaContext();

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, withAuth)
      .then(() => setAuthed(true))
      .catch(() => setAuthed(false));
  }, []);

  useEffect(() => {
    if (authed !== true) return;
    const load = (path, setter, label) =>
      axios.get(`${API}/admin/${path}`, withAuth).then((r) => setter(r.data)).catch(() => toast.error(`Could not load ${label}`));
    load("newsletter", setSubs, "subscribers");
    load("contacts", setContacts, "contact messages");
    load("orders", setOrders, "orders");
    load("reviews", setReviews, "reviews");
  }, [authed]);

  const deleteReview = async (id) => {
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, withAuth);
      setReviews((prev) => prev.filter((r) => r.id !== id));
      toast.success("Review deleted");
    } catch (e) {
      toast.error("Could not delete review");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, withAuth);
    } catch {
      // token may already be invalid; local logout still proceeds
    }
    setAuthed(false);
  };

  if (authed === null) {
    return (
      <main className="pt-32 min-h-screen flex justify-center" data-testid="admin-loading">
        <Loader2 className="animate-spin text-[#BA8D53]" size={28} />
      </main>
    );
  }
  if (authed === false) return <AdminLogin onSuccess={() => setAuthed(true)} />;

  const TABS = [
    { key: "media", label: "Media Library", icon: ImageIcon },
    { key: "orders", label: `Orders (${orders.length})`, icon: Package },
    { key: "discounts", label: "Discounts", icon: Tag },
    { key: "reviews", label: `Reviews (${reviews.length})`, icon: Star },
    { key: "newsletter", label: `Newsletter (${subs.length})`, icon: Mail },
    { key: "contacts", label: `Enquiries (${contacts.length})`, icon: MessageSquare },
    { key: "instagram", label: "Instagram", icon: Instagram },
  ];

  return (
    <main data-testid="admin-page" className="pt-24 md:pt-32 min-h-screen">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10 pb-28">
        <p className="overline-label">House Administration</p>
        <div className="flex items-end justify-between">
          <h1 className="font-serif-brand text-5xl md:text-6xl font-light mt-4">Admin</h1>
          <button data-testid="admin-logout-button" onClick={logout} className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.2em] font-medium text-black/50 hover:text-black transition-colors">
            <LogOut size={14} strokeWidth={1.5} /> Sign Out
          </button>
        </div>

        <div className="mt-10 flex flex-wrap gap-2 border-b border-black/10">
          {TABS.map((t) => (
            <button
              key={t.key}
              data-testid={`admin-tab-${t.key}`}
              onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-5 py-3 text-[0.7rem] uppercase tracking-[0.2em] font-medium transition-colors ${
                tab === t.key ? "bg-[#111] text-[#F7F3EE]" : "text-black/50 hover:text-black"
              }`}
            >
              <t.icon size={14} strokeWidth={1.5} /> {t.label}
            </button>
          ))}
        </div>

        {tab === "media" && (
          <div className="mt-12 space-y-16" data-testid="admin-media-section">
            <p className="text-sm text-black/55 max-w-xl -mb-6">
              Replace the temporary placeholder imagery with your actual Reserve 30 photography. Changes apply across the site instantly.
            </p>
            {SLOT_GROUPS.map((g) => (
              <div key={g.group}>
                <h2 className="font-serif-brand text-3xl font-light mb-6">{g.group}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {g.slots.map((s) => (
                    <MediaSlot key={s.key} slot={s} current={media[s.key]} onUploaded={refresh} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "orders" && (
          <div className="mt-12 space-y-4" data-testid="admin-orders-list">
            {orders.length === 0 ? (
              <p className="text-black/50">No orders yet.</p>
            ) : (
              orders.map((o) => (
                <div key={o.id} className="border border-black/10 p-6 bg-white/40" data-testid={`admin-order-${o.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <p className="text-sm font-medium">
                      {o.customer?.name} <span className="text-black/40">· {o.customer?.email} · {o.customer?.phone}</span>
                    </p>
                    <div className="flex items-center gap-2">
                      <span className={`px-2.5 py-1 text-[0.6rem] uppercase tracking-[0.18em] ${STATUS_BADGE[o.status] || "bg-black/10"}`}>
                        {o.status}
                      </span>
                      <ShopifyBadge order={o} />
                    </div>
                  </div>
                  <p className="mt-1 text-xs text-black/40">{new Date(o.created_at).toLocaleString("en-IN")} · Order {o.id.slice(0, 8).toUpperCase()}</p>
                  <div className="mt-3 text-sm text-black/70 space-y-0.5">
                    {(o.items || []).map((i) => (
                      <p key={`${i.slug}-${i.variant}`}>{i.name} — {i.variant_label} × {i.qty} · ₹{Math.round(i.unit_price * i.qty).toLocaleString("en-IN")}</p>
                    ))}
                  </div>
                  <p className="mt-2 text-sm font-medium">
                    Total ₹{Math.round(o.total).toLocaleString("en-IN")}{" "}
                    <span className="text-black/40 font-normal">
                      (shipping ₹{o.shipping}{o.discount_amount ? ` · discount ${o.discount_code} −₹${Math.round(o.discount_amount).toLocaleString("en-IN")}` : ""})
                    </span>
                  </p>
                  <p className="mt-1 text-xs text-black/45">{o.customer?.address}, {o.customer?.city}, {o.customer?.state} — {o.customer?.pincode}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "discounts" && <AdminDiscounts />}
        {tab === "instagram" && <AdminInstagram />}

        {tab === "reviews" && (
          <div className="mt-12 space-y-4" data-testid="admin-reviews-list">
            {reviews.length === 0 ? (
              <p className="text-black/50">No customer reviews yet. Reviews submitted from the Track Order page appear here and on the Word of Mouth page.</p>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="border border-black/10 p-6 bg-white/40" data-testid={`admin-review-${r.id}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-0.5 text-[#BA8D53]">
                        {Array.from({ length: r.rating }).map((_, s) => (
                          <Star key={`${r.id}-s-${s}`} size={13} fill="currentColor" strokeWidth={0} />
                        ))}
                      </div>
                      <p className="font-medium text-sm">{r.name} <span className="text-black/40">· {r.city}</span></p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="text-black/40 text-xs">{new Date(r.created_at).toLocaleString("en-IN")}</span>
                      <button
                        onClick={() => deleteReview(r.id)}
                        data-testid={`admin-review-delete-${r.id}`}
                        title="Delete review"
                        className="text-black/40 hover:text-red-700 transition-colors"
                      >
                        <Trash2 size={15} strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                  <p className="mt-3 text-sm text-black/70 leading-relaxed italic">"{r.text}"</p>
                  <p className="mt-2 text-xs text-black/35">Order {r.order_id.slice(0, 8).toUpperCase()}</p>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "newsletter" && (
          <div className="mt-12" data-testid="admin-newsletter-list">
            {subs.length === 0 ? (
              <p className="text-black/50">No subscribers yet.</p>
            ) : (
              <div className="border border-black/10 divide-y divide-black/10">
                {subs.map((s) => (
                  <div key={s.id} className="flex justify-between items-center px-5 py-4 text-sm">
                    <span className="font-medium">{s.email}</span>
                    <span className="text-black/40 text-xs">{new Date(s.created_at).toLocaleString("en-IN")}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "contacts" && (
          <div className="mt-12 space-y-4" data-testid="admin-contacts-list">
            {contacts.length === 0 ? (
              <p className="text-black/50">No enquiries yet.</p>
            ) : (
              contacts.map((c) => (
                <div key={c.id} className="border border-black/10 p-6 bg-white/40">
                  <div className="flex flex-wrap justify-between gap-2">
                    <p className="font-medium text-sm">{c.name} <span className="text-black/40">· {c.email}</span></p>
                    <span className="text-black/40 text-xs">{new Date(c.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  {c.subject && <p className="mt-2 text-[0.68rem] uppercase tracking-[0.2em] text-[#BA8D53]">{c.subject}</p>}
                  <p className="mt-3 text-sm text-black/70 leading-relaxed whitespace-pre-wrap">{c.message}</p>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </main>
  );
}
